import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import { useTheme } from './context/ThemeContext';
import { BlogProvider } from './context/BlogContext';
import api from './utils/api';

// Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ProjectPage from './pages/ProjectPage';
import ContactPage from './pages/ContactPage';
import BlogPosts from './pages/BlogPosts';
import SinglePost from './pages/SinglePost';
import PhotoGallery from './pages/PhotoGallery';

// Auth Pages
import AuthPage from './authPages/AuthPage';
import Dashboard from './authPages/Dashboard';

// Components
import Navbar from './components/NavBar';
import NotFound from './components/NotFound';
import ScrollToTop from './components/ScrollToTop';

// Styles
import './App.css';
function App() {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    
    useEffect(() => {
        const fetchUser = async () => {
            if (token) {
                try {
                    const response = await api.get('/user/me');
                    setUser(response.data);
                } catch (error) {
                    console.error('Error fetching user data:', error);
                    // Handle error accordingly (e.g., logout if token is invalid)
                    setToken(null);
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                }
            }
        };
        fetchUser();
    }, [token]);

    const handleLogout = () => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Redirect to home page after logout
        window.location.href = '/';
    };

    return (
        <Router>
            <BlogProvider>
                <AppContent 
                    user={user} 
                    token={token}
                    onLogout={handleLogout}
                    setToken={setToken}
                />
            </BlogProvider>
        </Router>
    );
}

const AppContent = ({ user, token, onLogout, setToken }) => {
    const { darkMode } = useTheme();
    const location = useLocation();
    const pageVariants = {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -10 }
    };

    // Check if current route is dashboard
    const isDashboard = location.pathname === '/dashboard';

    return (
        <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
            {/* Navbar - hidden on dashboard */}
            {!isDashboard && <Navbar />}
            
            <div className={isDashboard ? '' : 'pt-20'}>
                <ScrollToTop />
                <AnimatePresence mode="wait">
                    <Routes location={location} key={location.pathname}>
                        <Route path="/" element={
                            <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                <HomePage />
                            </motion.div>
                        } />
                        
                        <Route path="/about" element={
                            <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                <AboutPage />
                            </motion.div>
                        } />
                        
                        <Route path="/projects" element={
                            <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                <ProjectPage />
                            </motion.div>
                        } />
                        
                        <Route path="/my-blogs" element={
                            <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                <BlogPosts />
                            </motion.div>
                        } />
                        
                        <Route path="/contact" element={
                            <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                <ContactPage />
                            </motion.div>
                        } />
                        
                        <Route path="/blog-view/:id" element={
                            <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                <SinglePost />
                            </motion.div>
                        } />
                        
                        <Route path="/register" element={
                            <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                <AuthPage setToken={setToken} />
                            </motion.div>
                        } />
                        
                        <Route path="/login" element={
                            token ? <Navigate to="/dashboard" replace /> : (
                                <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                    <AuthPage setToken={setToken} />
                                </motion.div>
                            )
                        } />
                        
                        <Route path="/forgot-password" element={
                            <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                <AuthPage setToken={setToken} />
                            </motion.div>
                        } />
                        
                        <Route path="/reset-password/:token?" element={
                            <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                <AuthPage setToken={setToken} />
                            </motion.div>
                        } />
                        
                        <Route path="/dashboard" element={
                            token ? (
                                <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                    <Dashboard onLogout={onLogout} user={user} />
                                </motion.div>
                            ) : <Navigate to="/login" replace />
                        } />
                        
                        <Route path="/gallery" element={
                            <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                <PhotoGallery />
                            </motion.div>
                        } />
                        
                        <Route path="/page-not-found" element={
                            <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants} transition={{ duration: 0.25 }}>
                                <NotFound />
                            </motion.div>
                        } />
                        
                        <Route path="*" element={<Navigate to="/page-not-found" replace />} />
                    </Routes>
                </AnimatePresence>
            </div>
            
            <Toaster 
                position="bottom-center"
                toastOptions={{
                    duration: 3000,
                    background: darkMode ? '#1f2937' : '#ffffff',
                    color: darkMode ? '#ffffff' : '#1f2937',
                    className: 'text-sm',
                }}
            />
        </div>
    );
}

export default App;
